import type { SelectionList } from '@revelio/iso-utility';

import {
  resolveSelectionListLabelById,
  resolveStaticListLabelById,
} from '../ai-search/transformAiResponseToCustomDashFilters/selection';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
  isSegmentedFilter,
  type ListFilter,
  type ListSelection,
  type PrimarySelection,
  type RangeFilter,
  type StaticListFilter,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { createSegmentedFilter } from './common';

export function selectionNameLooksUnresolved(
  id: string,
  name: string
): boolean {
  const idStr = String(id);
  const nameStr = String(name);
  if (nameStr === idStr) return true;
  const trimmed = nameStr.trim();
  if (trimmed !== '' && /^\d+$/.test(trimmed)) return true;
  if (idStr.includes('_')) {
    const last = idStr.split('_').pop();
    if (last !== undefined && nameStr === last) return true;
  }
  return false;
}

function patchTreeSelections(
  filter: TreeFilter,
  selectionLists: SelectionList[]
): TreeFilter | null {
  const patchSel = <T extends TreeSelection>(sel: T): T => {
    if (!selectionNameLooksUnresolved(sel.id, sel.name)) {
      return sel;
    }
    const leaf = sel.id.split('_').pop();
    if (leaf === undefined) return sel;
    const numericId = Number(leaf);
    if (!Number.isFinite(numericId)) return sel;
    const resolved = resolveSelectionListLabelById({
      selectionListId: String(sel.levelId),
      numericId,
      rawId: sel.id,
      selectionLists,
    });
    if (resolved === null || resolved === sel.name) return sel;
    return { ...sel, name: resolved };
  };

  let changed = false;
  const selections = filter.selections.map((s) => {
    const next = patchSel(s);
    if (next !== s) changed = true;
    return next;
  });

  let primarySelections: PrimarySelection[] | undefined;
  if (Array.isArray(filter.primarySelections)) {
    primarySelections = filter.primarySelections.map((s) => {
      const next = patchSel(s);
      if (next !== s) changed = true;
      return next;
    });
  }

  if (!changed) return null;
  return {
    ...filter,
    selections,
    ...(primarySelections !== undefined && { primarySelections }),
  };
}

function patchListSelections(
  filter: ListFilter,
  selectionListId: string,
  selectionLists: SelectionList[]
): ListFilter | null {
  let changed = false;
  const selections = filter.selections.map((sel: ListSelection) => {
    if (!selectionNameLooksUnresolved(sel.id, sel.name)) return sel;
    const numericId = Number(sel.id);
    if (!Number.isFinite(numericId)) return sel;
    const resolved = resolveSelectionListLabelById({
      selectionListId,
      numericId,
      selectionLists,
    });
    if (resolved === null || resolved === sel.name) return sel;
    changed = true;
    return { ...sel, name: resolved };
  });
  return changed ? { ...filter, selections } : null;
}

function patchStaticListSelections(
  filter: StaticListFilter
): StaticListFilter | null {
  if (
    filter.id !== FilterId.FLIGHT_RISK &&
    filter.id !== FilterId.PRESTIGE &&
    filter.id !== FilterId.REMOTE_SUITABILITY
  ) {
    return null;
  }

  const staticFilterId = filter.id as
    | FilterId.FLIGHT_RISK
    | FilterId.PRESTIGE
    | FilterId.REMOTE_SUITABILITY;

  let changed = false;
  const selections = filter.selections.map((sel: ListSelection) => {
    if (!selectionNameLooksUnresolved(sel.id, sel.name)) return sel;
    const numericId = Number(sel.id);
    if (!Number.isFinite(numericId)) return sel;
    const resolved = resolveStaticListLabelById({
      filterId: staticFilterId,
      numericId,
    });
    if (resolved === sel.name) return sel;
    changed = true;
    return { ...sel, name: resolved };
  });
  return changed ? { ...filter, selections } : null;
}

/**
 * Returns a new filter with selection display names resolved from `selectionLists` when
 * names look like unresolved ids. Returns null when nothing changes.
 */
function patchFilterUnresolvedSelectionNames(
  filter: Filter,
  selectionLists: SelectionList[]
): Filter | null {
  if (isMultiFilter(filter)) {
    let changed = false;
    const instances = filter.instances.map((inst) => {
      const patched = patchFilterUnresolvedSelectionNames(inst, selectionLists);
      if (patched === null) return inst;
      changed = true;
      return patched;
    });
    return changed ? ({ ...filter, instances } as Filter) : null;
  }

  if (isSegmentedFilter(filter)) {
    const patchedWrapped = patchFilterUnresolvedSelectionNames(
      filter.wrappedFilter,
      selectionLists
    );
    let changed = patchedWrapped !== null;
    const wrapped = patchedWrapped ?? filter.wrappedFilter;

    const segmentations = filter.segmentations ?? {};
    const nextSegs: Record<string, Filter> = { ...segmentations };
    for (const [key, seg] of Object.entries(segmentations)) {
      const patchedSeg = patchFilterUnresolvedSelectionNames(
        seg,
        selectionLists
      );
      if (patchedSeg !== null) {
        nextSegs[key] = patchedSeg;
        changed = true;
      }
    }

    if (!changed) return null;

    return createSegmentedFilter({
      wrappedFilter: wrapped as
        | TreeFilter
        | ListFilter
        | StaticListFilter
        | RangeFilter,
      baseProps: filter,
      segmentations: nextSegs,
    });
  }

  if (filter.type === FilterType.TREE) {
    return patchTreeSelections(filter, selectionLists);
  }

  if (filter.type === FilterType.LIST) {
    const def = getFilterDefinition(filter.id);
    if (!def || def.type !== FilterType.LIST) return null;
    return patchListSelections(filter, def.selectionListId, selectionLists);
  }

  if (filter.type === FilterType.STATIC_LIST) {
    return patchStaticListSelections(filter);
  }

  return null;
}

/**
 * Returns a map suitable for UI (tags, summaries): replaces placeholder selection names
 * using loaded `selectionLists`. If lists are empty, returns `filterMap` unchanged.
 * Reuses the same map instance when no selection names change.
 */
export function applyResolvedSelectionLabelsToFilterMap(
  filterMap: FilterSelectionsMap,
  selectionLists: SelectionList[]
): FilterSelectionsMap {
  if (selectionLists.length === 0) {
    return filterMap;
  }

  const next = new Map<FilterId, Filter>();
  let anyFilterChanged = false;

  for (const [filterId, filter] of filterMap.entries()) {
    const withLabels = patchFilterUnresolvedSelectionNames(
      filter,
      selectionLists
    );
    if (withLabels !== null) {
      next.set(filterId, withLabels);
      anyFilterChanged = true;
    } else {
      next.set(filterId, filter);
    }
  }

  return anyFilterChanged ? next : filterMap;
}
